/**
 * @file Item-scoped live-save queue for registered detail controls.
 */

import { state } from '../../app/state/state.js';
import {
    getItemType,
} from '../utils/itemLookup.js';

function cloneRegisteredDetailValue(value) {
    return Array.isArray(value) ? [...value] : value;
}

function getRegisteredDetailSaveKey(save) {
    return `${save.itemId || ''}:${save.fieldName || ''}`;
}

function createRegisteredDetailSave({ item, itemType, fieldName, value }) {
    return {
        itemId: item?.id || state.selectedItemId || '',
        itemType: itemType || getItemType(item?.id) || '',
        fieldName,
        value: cloneRegisteredDetailValue(value),
    };
}

/**
 * Creates a live-save queue that serializes detail control writes.
 * @param {object} config Queue dependencies.
 * @param {Function} config.handleDetailFieldChange Field persistence callback.
 * @param {Function} [config.onSaveFailure] Called after a queued save fails and
 * optimistic queue state has been cleared.
 * @returns {object} Queue helpers.
 */
export function createDetailLiveSaveQueue({
    handleDetailFieldChange,
    onSaveFailure = () => {},
}) {
    let activeRegisteredDetailSave = null;
    const pendingRegisteredDetailSaves = new Map();

    function shiftPendingRegisteredDetailSave() {
        const nextSave = pendingRegisteredDetailSaves.values().next().value || null;
        if (nextSave) {
            pendingRegisteredDetailSaves.delete(getRegisteredDetailSaveKey(nextSave));
        }
        return nextSave;
    }

    async function persistRegisteredDetailSave(save) {
        return handleDetailFieldChange({
            checked: false,
            disabled: false,
            name: save.fieldName,
            type: 'text',
            value: save.value,
        }, {
            itemId: save.itemId,
            itemType: save.itemType,
            suppressFailureRender: true,
        });
    }

    return {
        /**
         * Returns optimistic live-save values for an item.
         * @param {string} itemId Detail item ID.
         * @returns {object} Pending values keyed by field name.
         */
        getOptimisticDetailValues(itemId) {
            const values = {};
            if (!itemId) {return values;}

            if (activeRegisteredDetailSave?.itemId === itemId) {
                values[activeRegisteredDetailSave.fieldName] =
                    cloneRegisteredDetailValue(activeRegisteredDetailSave.value);
            }

            pendingRegisteredDetailSaves.forEach((save) => {
                if (save.itemId !== itemId) {return;}
                values[save.fieldName] = cloneRegisteredDetailValue(save.value);
            });
            return values;
        },

        /**
         * Queues or immediately persists a registered detail control value.
         * @param {object} params Save parameters.
         * @param {object} params.item Detail item.
         * @param {string} params.itemType Detail item type.
         * @param {string} params.fieldName Field name.
         * @param {*} params.value Canonical field value.
         */
        async saveRegisteredDetailValue({ item, itemType, fieldName, value }) {
            const save = createRegisteredDetailSave({
                item,
                itemType,
                fieldName,
                value,
            });

            if (activeRegisteredDetailSave) {
                pendingRegisteredDetailSaves.set(getRegisteredDetailSaveKey(save), save);
                return;
            }

            try {
                let nextSave = save;
                while (nextSave) {
                    activeRegisteredDetailSave = nextSave;
                    const success = await persistRegisteredDetailSave(nextSave);
                    if (success === false) {
                        const failedSave = nextSave;
                        activeRegisteredDetailSave = null;
                        onSaveFailure(failedSave);
                    }
                    nextSave = shiftPendingRegisteredDetailSave();
                }
            } finally {
                activeRegisteredDetailSave = null;
            }
        },
    };
}
